import * as React from 'react';

import Input, { InputProps } from '../../atoms/Input';

const PERCENTAGE_REGEX = /\D*(\d+|\d.*?\d)(?:,(\d{1,3}))?\D*$/;

export interface Props extends Omit<InputProps, 'type'> {
  /** Percentage value */
  value?: string;
  decimalDigits?: 1 | 2;
  allowsDecimal?: boolean;
}

const PercentageInput = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      className,
      value,
      allowsDecimal = false,
      decimalDigits = 1,
      onChange,
      onFocus,
      ...props
    },
    ref,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) ?? innerRef;

    /**
     * Formats percentage with Intl
     */
    const formatPercentage = React.useCallback(
      (textValue: number | '') => {
        if (!textValue && textValue !== 0) return '';
        if (Number.isNaN(textValue)) return '';

        return new Intl.NumberFormat('pt-BR', {
          style: 'percent',
          minimumFractionDigits: allowsDecimal ? decimalDigits : 0,
          maximumFractionDigits: allowsDecimal ? decimalDigits : 0,
        }).format(textValue);
      },
      [allowsDecimal, decimalDigits],
    );

    const [displayValue, setDisplayValue] = React.useState(
      formatPercentage(value ? parseFloat(value) : ''),
    );

    /**
     * Formats percentage and updates cursor position
     */
    const handleChange = React.useCallback<NonNullable<InputProps['onChange']>>(
      (e) => {
        const percentageParts = PERCENTAGE_REGEX.exec(e.target.value);
        const rawValue = percentageParts
          ? parseFloat(
              `${percentageParts[1].replaceAll(/\D/g, '')}.${
                allowsDecimal
                  ? percentageParts[2]?.slice(0, decimalDigits) || '00'
                  : '00'
              }`,
            ) / 100
          : '';
        const formattedValue = formatPercentage(rawValue);

        // Cursor
        const beforeCursor = e.target.value.slice(
          0,
          e.target.selectionStart || undefined,
        );
        const digitsBeforeCursor = beforeCursor.replace(/[^\d]/g, '').length;

        const nativeEvent = e.nativeEvent as InputEvent;

        // Updates display/onChange
        setDisplayValue(formattedValue);
        e.target.value = rawValue !== '' ? String(rawValue) : '';
        onChange?.(e);

        const justTypedComma =
          nativeEvent.inputType === 'insertText' && nativeEvent.data === ',';

        // Set Caret position
        window.requestAnimationFrame(() => {
          const inputEl = inputRef.current;
          if (!inputEl) return;

          let newCursor = 0;

          if (justTypedComma && formattedValue.includes(',')) {
            // Move cursor to the first digit after the comma
            newCursor = formattedValue.indexOf(',') + 1;
          } else {
            let digitCount = 0;
            let lastDigitPosition = 0;
            for (let i = 0; i < formattedValue.length; i += 1) {
              if (/\d/.test(formattedValue[i])) {
                digitCount += 1;
                lastDigitPosition = i;
              }
              if (digitCount === digitsBeforeCursor) {
                newCursor = i + 1;
                break;
              } else if (i === formattedValue.length - 1) {
                newCursor = lastDigitPosition + 1;
              }
            }
          }
          inputEl.setSelectionRange(newCursor, newCursor);
        });
      },
      [allowsDecimal, decimalDigits, formatPercentage, inputRef, onChange],
    );

    // Keep state in sync with external value
    React.useEffect(() => {
      setDisplayValue(formatPercentage(value ? parseFloat(value) : ''));
    }, [formatPercentage, value]);

    return (
      <Input
        className={className}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        {...props}
      />
    );
  },
);

export default PercentageInput;
